import React, {useEffect, useState, useRef} from 'react';
import {
    Box,
    Menu,
    ListItemText,
    SvgIcon,
    CircularProgress,
} from '@mui/material';
import {
    StyledFileManagerTypography,
    StyledFileManagerSpanTypography,
    StyledFileManagerIconButton,
} from '../styles/mui_styles';
import { motion } from 'framer-motion';
import ExportItem from './ExportItem';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {useGetExportsByEmailQuery} from '../services/requests';
import {useLazyGetExportByInstanceIdQuery} from '../services/requests';
import {useMsal} from '@azure/msal-react';
import { useShowSuccessSnackbar } from '../hooks/useShowSuccessSnackbar';

export default function FileManagerMenu() {
    const {instance} = useMsal();
    const activeAccount = instance.getActiveAccount();
    const email = activeAccount?.username;
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const previousStatusesRef = useRef([]);
    const {data: exports = [], isLoading, refetch} = useGetExportsByEmailQuery(email, {
        skip: !email,
    });

    const [exportStatuses, setExportStatuses] = useState([]);
    const [inProgressInstanceIds, setInProgressInstanceIds] = useState([]);
    const [inProgressCount, setInProgressCount] = useState(0);
    const [showBounce, setShowBounce] = useState(false);

    const [triggerGetExportByInstanceId] = useLazyGetExportByInstanceIdQuery();
    const showSuccessSnackbar = useShowSuccessSnackbar();

    const shownSnackbarsRef = useRef(new Set());

    const exportStatusesRef = useRef([]);

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const formatDate = (isoDateStr) => {
        const date = new Date(isoDateStr);
        return date.toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    useEffect(() => {
        exportStatusesRef.current = exportStatuses;
    }, [exportStatuses]);

    useEffect(() => {
        setExportStatuses(prev => {
            if (
                prev.length === exports.length &&
                prev.every((item, idx) =>
                    item.instanceId === exports[idx].instanceId &&
                    item.status === exports[idx].status
                )
            ) {
                return prev;
            }
            return exports;
        });
    }, [exports]);

    useEffect(() => {
        const handleStartExport = () => {
            setShowBounce(true);

            const timeouts = [0, 500, 1000, 1500].map(delay =>
                setTimeout(() => {
                    refetch().then(newData => {
                        if (newData?.data) {
                            setExportStatuses(newData.data);
                        }
                    });
                }, delay)
            );

            setTimeout(() => setShowBounce(false), 1500);

            return () => timeouts.forEach(clearTimeout);
        };

        window.addEventListener("startExport", handleStartExport);
        return () => {
            window.removeEventListener("startExport", handleStartExport);
        };
    }, [email, refetch]);

    useEffect(() => {
        if (!email) return;
        refetch().then(res => {
            if (res?.data) {
                const exportsData = res.data;
                const inProgressExports = exportsData.filter(e => e.status === 'InProgress');
                const instanceIds = inProgressExports.map(e => e.instanceId);
                setExportStatuses(exportsData);
                setInProgressInstanceIds(instanceIds);
            }
        });
    }, [email, refetch, inProgressCount]);

    useEffect(() => {
        const updatedInProgress = exportStatuses.filter(e => e.status === 'InProgress');
        setInProgressInstanceIds(updatedInProgress.map(e => e.instanceId));
        setInProgressCount(updatedInProgress.length);
    }, [exportStatuses]);

    useEffect(() => {
        exportStatuses.forEach(current => {
            const prev = previousStatusesRef.current.find(e => e.instanceId === current.instanceId);
            const notYetShown = !shownSnackbarsRef.current.has(current.instanceId);
            const nowCompleted = current.status === 'Completed';
            const wasInProgress = prev?.status === 'InProgress';

            if (nowCompleted && wasInProgress && notYetShown) {
                showSuccessSnackbar();
                shownSnackbarsRef.current.add(current.instanceId);
            }
        });

        previousStatusesRef.current = exportStatuses;
    }, [exportStatuses, showSuccessSnackbar]);



    useEffect(() => {
        if (inProgressInstanceIds.length === 0) return;

        let currentIndex = 0;

        const interval = setInterval(async () => {
            const instanceId = inProgressInstanceIds[currentIndex];

            try {
                const updatedExport = await triggerGetExportByInstanceId(instanceId).unwrap();
                const existingExport = exportStatusesRef.current.find(e => e.instanceId === instanceId);

                if (existingExport && existingExport.status !== updatedExport.status) {
                    setExportStatuses(prev =>
                        prev.map(e => e.instanceId === instanceId ? updatedExport : e)
                    );
                }
            } catch (err) {
                console.error(`Error checking export status for ${instanceId}:`, err);
            }

            currentIndex = (currentIndex + 1) % inProgressInstanceIds.length;
        }, 500);

        return () => clearInterval(interval);
    }, [inProgressInstanceIds, triggerGetExportByInstanceId, showSuccessSnackbar]);

    return (
        <Box className="file_manager">
            <Box className="file_manager--block"  onClick={open ? handleClose : handleOpen}>
                <Box className="file_manager--icon_block">
                    <SvgIcon className="file_icon"/>
                    <StyledFileManagerTypography variant="body1">File Manager</StyledFileManagerTypography>
                </Box>
                <StyledFileManagerIconButton
                    sx={{
                        borderRadius: 0,
                        '.MuiTouchRipple-ripple .MuiTouchRipple-child': {
                            backgroundColor: 'transparent',
                        },
                    }}
                >
                    {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    {(exportStatuses.some(e => e.status === 'InProgress') || showBounce) && (
                        showBounce ? (
                            <motion.div
                                className="file_manager--alert"
                                initial={{ scale: 0 }}
                                animate={{ scale: [0.3, 1.05, 0.9, 1] }}
                                transition={{ duration: 1.5, ease: 'easeOut' }}
                            />
                        ) : (
                            <Box className="file_manager--alert" />
                        )
                    )}
                </StyledFileManagerIconButton>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: { width: "325px" },
                }}
                slotProps={{
                    root: { sx: { '.MuiList-root': { padding: 0 } } },
                }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 100,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : exports.length === 0 ? (
                    <Box className="no_found_block">
                        <SvgIcon className="no_found"/>
                        <ListItemText primary="No exports found" />
                    </Box>
                ) : (
                    <Box className="file_manager-menu_block">
                        {exportStatuses.map(item => {
                            const fileName = `Export File_${formatDate(item.createdAt)}`;
                            return <ExportItem key={item.instanceId} item={item} fileName={fileName} />;
                        })}
                    </Box>
                )}
                <Box className="file_manager-span-block">
                    <StyledFileManagerSpanTypography variant="caption" sx={{ px: 2, pt: 1 }}>
                        This list includes exports from the last 24 hours
                    </StyledFileManagerSpanTypography>
                </Box>
            </Menu>
        </Box>
    );
}
